import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { Sliders, GitFork, Plus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const CHOICES = { warmth: ["faible", "modérée", "élevée"], concision: ["faible", "modérée", "élevée"], humor_intensity: ["légère", "modérée"] };
const UNITS = ["minutes", "km", "metres", "denivele", "pourcentage", "semaines", "seances", "rpe", "heures_recup"];
const CATS = ["variations_allure", "fartlek", "intervalles_recup_longue", "fractionne_court", "fractionne_long", "vma", "seuil", "cotes", "sortie_longue", "volume", "intensite", "recup", "transition", "trail", "général"];
const SECTIONS = [
  ["debutant", "Débutant et marche/course"],
  ["fartlek", "Fartlek et fractionné"],
  ["seuil", "Seuil et côtes"],
  ["charge", "Charge et récupération"],
  ["rpe", "Retours, RPE et fatigue"],
  ["douleur", "Douleur et prudence"],
  ["sortie_longue", "Sortie longue"],
  ["transition", "Autres"],
  ["général", "Autres"],
];

export default function CoachMaiEngine() {
  const [tone, setTone] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nr, setNr] = useState({ rule_key: "", name: "", category: "général", scope: "global", scope_ref: "", value: "", unit: "minutes", op: ">=", blocking: true, status: "draft" });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/mai-config/tone").then((r) => setTone(r.data)),
      api.get("/mai-config/prog-rules").then((r) => setRules(r.data || [])),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const saveTone = async () => {
    try { await api.put("/mai-config/tone", tone); toast.success("Ton de MAI enregistré"); }
    catch { toast.error("Échec"); }
  };
  const createRule = async () => {
    if (!nr.rule_key || !nr.name) return toast.error("Clé et nom requis");
    try {
      const body = { ...nr, value: isNaN(Number(nr.value)) ? nr.value : Number(nr.value), scope_ref: nr.scope_ref || null };
      await api.post("/mai-config/prog-rules", body);
      toast.success("Règle créée (brouillon)"); setNr({ ...nr, rule_key: "", name: "", value: "" }); load();
    } catch { toast.error("Échec"); }
  };
  const setStatus = async (id, status) => {
    try { await api.post(`/mai-config/prog-rules/${id}/status`, { status }); toast.success(status); load(); }
    catch { toast.error("Échec"); }
  };
  const patchValue = async (id, value) => {
    const v = typeof value === "boolean" ? value : (isNaN(Number(value)) ? value : Number(value));
    try { await api.patch(`/mai-config/prog-rules/${id}`, { value: v }); toast.success("Mis à jour"); load(); }
    catch { toast.error("Échec"); }
  };

  if (loading || !tone) return <CoachLayout><div className="p-8"><Loader2 className="animate-spin" /></div></CoachLayout>;

  return (
    <CoachLayout>
      <div className="max-w-4xl mx-auto p-4 sm:p-8 space-y-10" data-testid="mai-engine-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Sliders className="w-6 h-6" /> Moteur MAI</h1>
          <p className="text-sm text-neutral-500 mt-1">Règle le ton conversationnel et les seuils de programmation, sans toucher au code. Les garde-fous de sécurité du ton restent non modifiables.</p>
        </div>

        {/* TON */}
        <section className="rounded-2xl border p-5 space-y-4" data-testid="mai-tone-section">
          <h2 className="font-semibold text-lg">Ton conversationnel</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {["warmth", "concision"].map((k) => (
              <label key={k} className="text-sm space-y-1">
                <span className="capitalize text-neutral-600">{k === "warmth" ? "Chaleur" : "Concision"}</span>
                <select data-testid={`tone-${k}`} className="w-full border rounded-lg px-3 py-2" value={tone[k]} onChange={(e) => setTone({ ...tone, [k]: e.target.value })}>
                  {CHOICES[k].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            ))}
            <label className="text-sm space-y-1">
              <span className="text-neutral-600">Humour</span>
              <select data-testid="tone-humor-enabled" className="w-full border rounded-lg px-3 py-2" value={tone.humor_enabled ? "oui" : "non"} onChange={(e) => setTone({ ...tone, humor_enabled: e.target.value === "oui" })}>
                <option value="oui">Activé</option><option value="non">Désactivé</option>
              </select>
            </label>
            <label className="text-sm space-y-1">
              <span className="text-neutral-600">Intensité de l'humour</span>
              <select data-testid="tone-humor-intensity" className="w-full border rounded-lg px-3 py-2" value={tone.humor_intensity} onChange={(e) => setTone({ ...tone, humor_intensity: e.target.value })}>
                {CHOICES.humor_intensity.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <label className="text-sm space-y-1">
              <span className="text-neutral-600">Max touches d'humour / réponse</span>
              <input data-testid="tone-max-humor" type="number" min="0" max="2" className="w-full border rounded-lg px-3 py-2" value={tone.max_humor_per_reply} onChange={(e) => setTone({ ...tone, max_humor_per_reply: Number(e.target.value) })} />
            </label>
          </div>
          <button data-testid="save-tone-btn" onClick={saveTone} className="rounded-full bg-black text-white px-5 py-2 text-sm font-medium flex items-center gap-2"><Check className="w-4 h-4" /> Enregistrer le ton</button>
        </section>

        {/* RÈGLES */}
        <section className="rounded-2xl border p-5 space-y-4" data-testid="mai-rules-section">
          <h2 className="font-semibold text-lg flex items-center gap-2"><GitFork className="w-5 h-5" /> Règles de programmation</h2>
          <p className="text-xs text-neutral-500 bg-neutral-50 border rounded-lg px-3 py-2" data-testid="rules-disclaimer">
            Ces valeurs servent de garde-fous au moteur MAI. Elles orientent l'éligibilité et la progression, mais ne remplacent pas l'analyse du profil, du réalisé et des retours du coureur.
          </p>
          <div className="space-y-6">
            {rules.length === 0 && <p className="text-sm text-neutral-500">Aucune règle. Les défauts prudents du moteur s'appliquent.</p>}
            {SECTIONS.map(([sec, label]) => {
              const items = rules.filter((r) => (r.section || r.category) === sec);
              if (items.length === 0) return null;
              return (
                <div key={sec} data-testid={`rules-group-${sec}`}>
                  <h3 className="text-sm font-semibold text-obsidian mb-2">{label}</h3>
                  <div className="space-y-2">
                    {items.map((r) => (
                      <div key={r.rule_id} data-testid={`rule-row-${r.rule_key}`} className="flex flex-wrap items-center gap-2 border rounded-xl px-3 py-2 text-sm">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === "active" ? "bg-green-100 text-green-700" : r.status === "draft" ? "bg-amber-100 text-amber-700" : "bg-neutral-100 text-neutral-500"}`}>{r.status}</span>
                        {r.wired_level === "fully_wired" || (r.wired && !r.wired_level)
                          ? <span data-testid={`wired-${r.rule_key}`} className="px-2 py-0.5 rounded-full text-xs bg-teal-100 text-teal-700">câblée</span>
                          : r.wired_level === "partially_wired"
                            ? <span data-testid={`partial-${r.rule_key}`} className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">Partiellement câblée — non appliquée en V1</span>
                            : <span data-testid={`unwired-${r.rule_key}`} className="px-2 py-0.5 rounded-full text-xs bg-neutral-100 text-neutral-500">Non disponible — non appliquée</span>}
                        <span className="font-medium">{r.name}</span>
                        <code className="text-xs text-neutral-400">{r.rule_key}</code>
                        <span className="text-neutral-500 text-xs">{r.effect || "—"} · {r.scope}{r.scope_ref ? `:${r.scope_ref}` : ""}</span>
                        {r.unit === "booleen"
                          ? <select data-testid={`rule-value-${r.rule_key}`} className="border rounded px-2 py-1 text-sm" defaultValue={r.value ? "true" : "false"} onChange={(e) => patchValue(r.rule_id, e.target.value === "true")}>
                              <option value="true">activé</option>
                              <option value="false">désactivé</option>
                            </select>
                          : <input data-testid={`rule-value-${r.rule_key}`} className="w-20 border rounded px-2 py-1 text-sm" defaultValue={r.value} onBlur={(e) => e.target.value != r.value && patchValue(r.rule_id, e.target.value)} />}
                        <span className="text-neutral-400 text-xs">{r.unit !== "booleen" ? r.unit + " · " : ""}{r.op} · v{r.version}{r.updated_by ? ` · ${r.updated_by}` : ""}</span>
                        <div className="ml-auto flex gap-1">
                          {r.status !== "active" && (
                            <button data-testid={`activate-${r.rule_key}`} disabled={!r.wired} title={r.wired ? "" : "Non câblée : activation impossible"}
                              onClick={() => r.wired && setStatus(r.rule_id, "active")}
                              className={`text-xs px-2 py-1 rounded text-white ${r.wired ? "bg-green-600" : "bg-neutral-300 cursor-not-allowed"}`}>Activer</button>
                          )}
                          {r.status === "active" && <button onClick={() => setStatus(r.rule_id, "archived")} className="text-xs px-2 py-1 rounded bg-neutral-200">Archiver</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t pt-4 space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Nouvelle règle</h3>
            <div className="grid sm:grid-cols-2 gap-2 text-sm">
              <input data-testid="nr-key" placeholder="rule_key (ex: min_continuous_for_fartlek)" className="border rounded-lg px-3 py-2" value={nr.rule_key} onChange={(e) => setNr({ ...nr, rule_key: e.target.value })} />
              <input data-testid="nr-name" placeholder="Nom lisible" className="border rounded-lg px-3 py-2" value={nr.name} onChange={(e) => setNr({ ...nr, name: e.target.value })} />
              <select data-testid="nr-category" className="border rounded-lg px-3 py-2" value={nr.category} onChange={(e) => setNr({ ...nr, category: e.target.value })}>{CATS.map((c) => <option key={c}>{c}</option>)}</select>
              <select data-testid="nr-scope" className="border rounded-lg px-3 py-2" value={nr.scope} onChange={(e) => setNr({ ...nr, scope: e.target.value })}><option value="global">global</option><option value="family">family</option><option value="template">template</option></select>
              {nr.scope !== "global" && <input placeholder="famille ou template_id" className="border rounded-lg px-3 py-2" value={nr.scope_ref} onChange={(e) => setNr({ ...nr, scope_ref: e.target.value })} />}
              <input data-testid="nr-value" placeholder="Valeur" className="border rounded-lg px-3 py-2" value={nr.value} onChange={(e) => setNr({ ...nr, value: e.target.value })} />
              <select data-testid="nr-unit" className="border rounded-lg px-3 py-2" value={nr.unit} onChange={(e) => setNr({ ...nr, unit: e.target.value })}>{UNITS.map((u) => <option key={u}>{u}</option>)}</select>
              <select data-testid="nr-op" className="border rounded-lg px-3 py-2" value={nr.op} onChange={(e) => setNr({ ...nr, op: e.target.value })}>{[">=", "<=", "==", ">", "<"].map((o) => <option key={o}>{o}</option>)}</select>
              <label className="flex items-center gap-2"><input type="checkbox" checked={nr.blocking} onChange={(e) => setNr({ ...nr, blocking: e.target.checked })} /> Bloquante</label>
            </div>
            <button data-testid="create-rule-btn" onClick={createRule} className="rounded-full bg-black text-white px-5 py-2 text-sm font-medium">Créer (brouillon)</button>
          </div>
        </section>
      </div>
    </CoachLayout>
  );
}
